import React, { useState, ChangeEvent } from 'react';
import { useLocation } from 'wouter';
import { useLocalize } from 'localize-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ContentHeader } from '@transferz/fe-component-library/src/components/contentHeader/ContentHeader';
import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import Input from '@transferz/fe-component-library/src/components/input/Input';
import User from '@transferz/fe-component-library/src/components/icons/UserIcon';
import Hashtag from '@transferz/fe-component-library/src/components/icons/HashtagIcon';
import Notification from '@transferz/fe-component-library/src/components/notification/Notification';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import { getLocalStorageItem } from '../../utils/utils';
import './login.scss';

const Login: React.FC<any> = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const bookingCodeReference = searchParams.get('bookingCode') || '';
  const lastNameReference = getLocalStorageItem<string>('userIdentity') || '';
  const { translate } = useLocalize();
  const [, setLocation] = useLocation();
  const flags = useFlags();
  const [bookingCode, setBookingCode] = useState(bookingCodeReference);
  const [lastName, setLastName] = useState(lastNameReference);
  const [bookingCodeError, setBookingCodeError] = useState('');
  const [lastNameError, setLastNameError] = useState('');

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const trimmedBookingCode = bookingCode.trim();
    const trimmedLastName = lastName.trim();
    const bookingCodeLength = trimmedBookingCode.length;
    const lastNameLength = trimmedLastName.length;

    if (!bookingCodeLength) {
      setBookingCodeError(translate('referenceRequired'));
      return false;
    }
    if (!lastNameLength) {
      setLastNameError(translate('lastNameRequired'));
      return false;
    }

    setLocation(`/${trimmedBookingCode}/${trimmedLastName}`);
  };

  const customWidgetStyles = {
    '--widget-background-color': window?.tz_globalConfigs?.styling?.widgetBackgroundColor || '#fc3',
    '--primary-button-color': window?.tz_globalConfigs?.styling?.primaryButtonColor || '#48a947',
  } as React.CSSProperties;

  return (
    <div className={`login ${flags?.newLoginDesign ? 'login-wrapper' : ''}`} style={customWidgetStyles}>
      <div className="login-container">
        {flags?.newLoginDesign
          ? (
            <ContentHeader classes="login" backgroundColor="#e6f4f8">
              <div className="login-container-form">
                <div className="form-background">
                  <Typography variant="h4" className="welcome-title">
                    {translate('welcomeMessage')}
                  </Typography>
                  <Typography variant="h5" className="login-text">
                    {translate('login')}
                  </Typography>
                  <div className="form-container">
                    {bookingCodeReference && (
                      <Notification
                        variant={NotificationVariants.error}
                        message={translate('noData')}
                        closeIcon={false}
                        className="no-data"
                      />
                    )}
                    <form id="login-form" className="form" onSubmit={handleSubmit}>
                      <div className="input-field booking-code">
                        <Input
                          name="booking-code"
                          label={translate('bookingReference')}
                          type="text"
                          value={bookingCode}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setBookingCode(event.target.value);
                            setBookingCodeError(event.target.value ? '' : translate('referenceRequired'));
                          }}
                          confirmed={!!bookingCode && !bookingCodeError}
                          error={bookingCodeError}
                          iconLeft={<Hashtag width={24} />}
                        />
                      </div>
                      <div className="input-field">
                        <Input
                          name="last-name"
                          label={translate('lastName')}
                          type="text"
                          value={lastName}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setLastName(event.target.value);
                            setLastNameError(event.target.value ? '' : translate('lastNameRequired'));
                          }}
                          confirmed={!!lastName && !lastNameError}
                          error={lastNameError}
                          iconLeft={<User />}
                        />
                      </div>
                    </form>
                  </div>
                  <Typography variant="body1" className="description-text">
                    {translate('findBookingReference')}
                  </Typography>
                  <Typography variant="body1" className="contact-text">
                    {translate('cantFind')} <a href="https://taxi2airport.freshdesk.com/en/support/home" target="_blank">{translate('contactSupport')}</a>
                  </Typography>
                  <Button
                    priority="primary"
                    variant="confirm"
                    label={translate('login')}
                    formId="login-form"
                    type="submit"
                    disabled={!!bookingCodeError || !!lastNameError}
                  />
                </div>
              </div>
            </ContentHeader>
          )
          : (
            <>
              {flags?.showNewDesign && <ContentHeader />}
              <div className="login-container-form">
                <div className="form-background">
                  <div className="mobile-description-container">
                    <h2 className="title">{translate('login')}</h2>
                    <p className="description">{translate('findBookingReference')}</p>
                    <p className="contact-us">{translate('cantFind')} <a href="https://taxi2airport.freshdesk.com/en/support/home" target="_blank">{translate('contactSupport')}</a></p>
                  </div>
                  <h2 className="title">{translate('login')}</h2>
                  <p className="description">{translate('findBookingReference')}</p>
                  <p className="contact-us">{translate('cantFind')} <a href="https://taxi2airport.freshdesk.com/en/support/home" target="_blank">{translate('contactSupport')}</a></p>
                  <div className="form-container">
                    {bookingCodeReference && (
                      <Notification
                        variant={NotificationVariants.error}
                        message={translate('noData')}
                        closeIcon={false}
                        className="no-data"
                      />
                    )}
                    <form className="form" onSubmit={handleSubmit}>
                      <div className="form-input">
                        <label>{translate('bookingReference')}</label>
                        <input type="text" value={bookingCode} onChange={e => {
                          setBookingCode(e.target.value);
                          setBookingCodeError('');
                        }} />
                        <span className="error">{bookingCodeError}</span>
                      </div>
                      <div className="form-input">
                        <label>{translate('lastName')}</label>
                        <input type="text" value={lastName} onChange={e => {
                          setLastName(e.target.value);
                          setLastNameError('');
                        }} />
                        <span className="error">{lastNameError}</span>
                      </div>
                      <button className="form-btn" type="submit">
                        {translate('login')}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </>
          )
        }
      </div>
    </div>
  );
};

export default Login;
